import {
    stringify,
} from 'qs';
import {
    buffers,
    eventChannel,
    END,
} from 'redux-saga';

function apiFetch(method, endpoint, {queryParams, ...options} = {}) {
    let queryString = '';
    if (queryParams) {
        queryString = stringify(JSON.parse(JSON.stringify(queryParams)), {
            addQueryPrefix: true,
        });
    }
    const url = `${endpoint}${queryString}`;
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    };

    return fetch(
        url,
        {
            method,
            headers,
            cache: 'no-cache',

            ...options,
        },
    ).then(response => {
        if (response.redirected) {
            location.reload();
        }
        if (response.ok) {
            return response.json();
        } else {
            return {status: 'error', error: response.status};
        }
    });
}

const apiGet = (endpoint, options = {}) => apiFetch('GET', endpoint, options);
const apiPost = (endpoint, options = {}) => apiFetch('POST', endpoint, options);
const apiDelete = (endpoint, options = {}) => apiFetch('DELETE', endpoint, options);

const loadTour = ({tour_id, password}) => apiPost(
    `/tour/${tour_id}/load`,
    {
        body: JSON.stringify({
            password,
        }),
    },
);

const tourEdit = ({
      tour_id,
      name,
      internal,
      showAddress,
      street,
      city,
      zip,
      showMap,
      latitude,
      longitude,
      contact,
      password,
      showLogo,
      showBottom,
      showGreeter,
  }) => {
    return apiPost(
        `/tour/${tour_id}/edit`,
        {
            body: JSON.stringify({
                name,
                internal,
                showAddress,
                street,
                city,
                zip,
                showMap,
                latitude,
                longitude,
                contact,
                password,
                showLogo,
                showBottom,
                showGreeter,
            }),
        },
    );
};

const previewDelete = ({tour_id}) => apiDelete(`/tour/${tour_id}/preview/delete`);

const floorCreate = ({tour_id, name}) => {
    return apiPost(
        `/tour/${tour_id}/floor/`,
        {
            body: JSON.stringify({
                name,
            }),
        },
    );
};

const floorReorder = ({tour_id, sortlist}) => {
    return apiPost(
        `/tour/${tour_id}/floor/reorder`,
        {
            body: JSON.stringify({
                sortlist,
            }),
        },
    );
};

const floorEdit = ({tour_id, floor_id, name}) => {
    return apiPost(
        `/tour/${tour_id}/floor/${floor_id}/`,
        {
            body: JSON.stringify({
                name,
            }),
        },
    );
};

function createUploadChannel(urlBuilder) {
    return ({files, ...params}) => {
        const url = urlBuilder({...params});
        return eventChannel(emitter => {
            const xhr = new XMLHttpRequest();

            const onProgress = (ev) => {
                if (ev.lengthComputable) {
                    const progress = ev.loaded / ev.total;
                    emitter({
                        progress,
                    });
                }
            };
            const onFailure = () => {
                emitter({
                    error: new Error('Upload failed'),
                });
                emitter(END);
            };
            xhr.upload.addEventListener('progress', onProgress);
            xhr.upload.addEventListener('error', onFailure);
            xhr.upload.addEventListener('abort', onFailure);
            xhr.onreadystatechange = () => {
                const {
                    readyState,
                    status,
                } = xhr;
                if (readyState === 4) {
                    if (status === 200) {
                        if (xhr.response !== null) {
                            emitter({
                                success: true,
                                response: xhr.response,
                            });
                        }
                        onFailure(null);
                    } else {
                        onFailure(null);
                    }
                }
            };
            xhr.responseType = 'json';
            xhr.open('POST', url, true);
            const data = new FormData();
            for (let x = 0; x < files.length; x++) {
                data.append(`files[${x}]`, files[x]);
            }
            xhr.send(data);

            return () => {
                xhr.upload.removeEventListener('progress', onProgress);
                xhr.upload.removeEventListener('error', onFailure);
                xhr.upload.removeEventListener('abort', onFailure);
                xhr.onreadystatechange = null;
                xhr.abort();
            };
        }, buffers.sliding(2));
    };
}

const panoramaUploadChannel = createUploadChannel(
    ({tour_id, floor_id}) => `/tour/${tour_id}/floor/${floor_id}/panorama`,
);

const floorplanUploadChannel = createUploadChannel(
    ({tour_id, floor_id}) => `/tour/${tour_id}/floor/${floor_id}/floorplan`,
);

const previewUploadChannel = createUploadChannel(({tour_id}) => `/tour/${tour_id}/preview`);

const roomCreate = ({tour_id, floor_id, name}) => {
    return apiPost(
        `/tour/${tour_id}/floor/${floor_id}/room/`,
        {
            body: JSON.stringify({
                name,
            }),
        },
    );
};

const roomReorder = ({tour_id, sortlist}) => {
    return apiPost(
        `/tour/${tour_id}/room/reorder`,
        {
            body: JSON.stringify({
                sortlist,
            }),
        },
    );
};

const roomEdit = ({tour_id, room_id, name, start_rotation, floor_id}) => {
    return apiPost(
        `/tour/${tour_id}/room/${room_id}/edit`,
        {
            body: JSON.stringify({
                name,
                start_rotation,
                floor_id,
            }),
        },
    );
};

const roomDelete = ({tour_id, room_id}) => apiDelete(`/tour/${tour_id}/room/${room_id}/`);

const floorDelete = ({tour_id, floor_id}) => apiDelete(`/tour/${tour_id}/floor/${floor_id}/`);

const floorplanDelete = ({tour_id, floor_id}) => apiDelete(`/tour/${tour_id}/floor/${floor_id}/floorplan_delete`);

const infopointSave = ({tour_id, floor_id, room_id, linked_room_id, u, v, title, symbol, text}) => {
    return apiPost(
        `/tour/${tour_id}/infopoint/`,
        {
            body: JSON.stringify({
                floor_id, room_id, linked_room_id, u, v, title, symbol, text,
            }),
        },
    );
};

const infopointEdit = ({tour_id, infopoint_id, linked_room_id, u, v, title, symbol, text}) => {
    return apiPost(
        `/tour/${tour_id}/infopoint/${infopoint_id}/`,
        {
            body: JSON.stringify({
                linked_room_id, u, v, title, symbol, text,
            }),
        },
    );
};

const infopointDelete = ({tour_id, infopoint_id}) => {
    return apiDelete(
        `/tour/${tour_id}/infopoint/${infopoint_id}/delete`,
    );
};

export default {
    apiFetch,
    apiGet,

    loadTour,
    tourEdit,
    previewDelete,

    floorCreate,
    floorEdit,
    floorReorder,
    floorDelete,
    floorplanDelete,

    roomCreate,
    roomReorder,
    roomEdit,
    roomDelete,

    infopointSave,
    infopointEdit,
    infopointDelete,

    panoramaUploadChannel,
    floorplanUploadChannel,

    previewUploadChannel,
};
